import { Config } from 'config/types';
import { AuthenticationInterface, ErrorReportInterface } from '../types';
import { ApiClient, createClient } from './api-client/api-client';
import {
	AuthMiddleware,
	LogMiddleware,
	ErrorHandlerMiddleware,
} from './middlewares';

export async function createApiClient(
	config: Config,
	authentication: AuthenticationInterface,
	errorReport: ErrorReportInterface
): Promise<ApiClient> {
	const api = createClient(config.api);

	// Configure middlewares using native axios interceptors
	const authMiddleware = new AuthMiddleware(authentication);
	const errorHandlerMiddleware = new ErrorHandlerMiddleware(errorReport);
	const logMiddleware = config.api.logEnabled ? new LogMiddleware() : null;

	// Request interceptor - runs in reverse order (last registered runs first)
	if (logMiddleware) {
		api.client.interceptors.request.use(
			async config => await logMiddleware.onRequest(config),
			error => Promise.reject(error)
		);
	}
	api.client.interceptors.request.use(
		async config => await authMiddleware.onRequest(config),
		error => Promise.reject(error)
	);

	// Response interceptor - runs in order (first registered runs first)
	if (logMiddleware) {
		api.client.interceptors.response.use(
			async response => await logMiddleware.onResponse(response),
			async error => {
				await logMiddleware.onResponseError(error);
				return Promise.reject(error);
			}
		);
	}
	api.client.interceptors.response.use(
		response => response,
		async error => {
			errorHandlerMiddleware.onResponseError(error);
			return Promise.reject(error);
		}
	);

	if (config.api.useMock) {
		const { setupBrowserWorker } = await import('./mock/browser');

		(await setupBrowserWorker(config.api)).start({
			quiet: true,
			onUnhandledRequest: 'bypass',
		});
	}

	return api;
}
